import { FaUndo, FaRupeeSign, FaCalendar, FaBoxOpen, FaWallet } from "react-icons/fa";

const formatDateTime = (d) => {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "";
  const day = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const time = date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${day}, ${time}`;
};

const PRODUCT_RETURN = "service_product_return";
const POS_RETURN = "pos_return";

// getParentChildOrders wraps each order as { _id, parent_order_id, details: {...} }
const unwrap = (raw) =>
  raw?.details
    ? { ...raw.details, _id: raw.details._id || raw._id }
    : raw;

const normalizeEntry = (raw, productLookup) => {
  const entry = unwrap(raw);
  const category = entry.order_category || entry.splitCase || entry.split_case;
  const isPos = category === POS_RETURN;

  const returned = Array.isArray(entry.return_service_products)
    ? entry.return_service_products
    : [];

  const isProductReturn = isPos || category === PRODUCT_RETURN || returned.length > 0;

  let lines;
  // 
  if (isPos) {
  const posItems = entry.items || entry.order_items || [];
  lines = posItems.map((item) => {
    const qty = Number(item.quantity) || 1;
    const unit = Number(item.unit_price ?? item.price) || 0; // GST-inclusive
    return {
      name:
        item.product_info?.product_details?.name ||
        item.service_name ||
        item.description ||
        "Product",
      sku: item.sku_number || "",          // new
      qty,
      reason: item.return_reason || entry.reason || entry.return_reason || "",
      note: item.note || entry.notes || entry.note || "",
      amount: qty * unit,
    };
  });
  } else {
    // Service product return
    lines = returned.map((p) => {
      const info = productLookup?.[`${p.service_ref_id}_${p.product_id}`] || {};
      const qty = Number(p.quantity) || 1;
      const price = Number(p.price) || 0;
      return {
        name: info.name || "Product",
        sku: info.sku || "",
        qty,
        reason: p.reason || "",
        note: p.note || "",
        amount: qty * price,
      };
    });
  }

  const amount = Number(
    entry.refund_amount ?? entry.returnAmount ?? entry.refundAmount ?? 0,
  );

  return {
    id: entry._id,
    reference: entry.order_number || "",
    isPos,
    isProductReturn,
    lines,
    amount: Number.isFinite(amount) ? amount : 0,
    notes: entry.notes || entry.note || "",
    date: entry.createdAt,
  };
};

const fmt = (n) =>
  Number.isInteger(n) ? n.toString() : n.toFixed(0);

const ReturnHistorySection = ({ entries = [], productLookup = {} }) => {
  if (!entries.length) return null;

  // Normalize first (unwrap `details`), then sort newest first
  const normalized = entries
    .map((raw) => normalizeEntry(raw, productLookup))
    .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  return (
    <div className="mt-0.5">
      <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2 mb-2">
        <FaUndo className="text-rose-500" /> Returns &amp; Refunds
      </h4>

      <div className="space-y-1">
        {normalized.map((e, idx) => {
          // Service product returns duplicate notes per line; POS notes are
          // order-level only, so show them there too.
         // const showNotes = e.notes && (!e.isProductReturn || e.isPos);
         const showNotes = !e.isProductReturn && e.notes;

          return (
            <div
              key={e.id || idx}
              className="border border-rose-200 bg-rose-50/40 rounded-xl p-3"
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-xs font-semibold text-rose-700 flex items-center gap-1">
                  {e.isProductReturn ? <FaBoxOpen size={12} /> : <FaWallet size={12} />}
                  {e.isProductReturn ? "Product Return" : "Refund"}
                  {/* {e.reference && (
                    <span className="font-normal text-gray-400 ml-1">· {e.reference}</span>
                  )} */}
                </span>
                {/* <span className="text-[11px] text-gray-400 flex items-center gap-1 whitespace-nowrap">
                  <FaCalendar size={10} /> {formatDateTime(e.date)}
                </span> */}
              </div>

              {e.isProductReturn && (
                <div className="space-y-1.5 mt-1">
                  {e.lines.map((l, i) => (
                    <div key={i} className="text-xs text-gray-700">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate font-medium">
                          {l.name} × {l.qty}
                        </span>
                        <span className="whitespace-nowrap font-medium">

                          ₹{fmt(l.amount)}
                        </span>
                      </div>
                      {l.sku && (
                          <div className="text-[11px] text-gray-400 truncate">SKU: {l.sku}</div>
                        )}
                      {l.reason && (
                        <div className="text-[11px] text-gray-500">
                          <span className="text-gray-400">Reason:</span> {l.reason}
                        </div>
                      )}
                      {l.note && (
                        <div className="text-[11px] text-gray-500">
                          <span className="text-gray-400">Note:</span> {l.note}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {showNotes && <p className="text-xs text-gray-600 mt-1">{e.notes}</p>}

              <div className="flex justify-between items-center mt-2 pt-2 border-t border-rose-200">
                <span className="text-[11px] text-gray-500">
                  {e.isProductReturn ? "Refund for returned items" : "Amount refunded"}
                </span>
                <span className="text-sm font-bold text-rose-700 flex items-center gap-0.5">
                  <FaRupeeSign size={11} />
                  {fmt(e.amount)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReturnHistorySection;